<mat-card *ngLet="loading$ | async as loading"
          class="settings-container">
    <form (submit)="onSubmit(settings)">
        <mat-tab-group (selectedTabChange)="activeTab = $event.tab.textLabel"
                       dynamicHeight="true"
                       mat-no-ink>
            <mat-tab [label]="TabLabel.General">
                <ng-template mat-tab-label>
                    <div data-cy="settings-general-tab">
                        <mat-icon *ngIf="general?.formGroup.dirty && !general?.formGroup.invalid"
                                  class="mat-accent"
                                  data-cy="settings-general-tab-changed-icon">edit</mat-icon>
                        {{ 'settings_tab_general' | translate }}
                    </div>
                </ng-template>
            </mat-tab>
            <mat-tab [label]="TabLabel.Deployment">
                <ng-template mat-tab-label>
                    <div data-cy="settings-deployment-tab">
                        <mat-icon *ngIf="!loading && deployment?.formGroup.invalid && deployment.isInitialized"
                                  class="mat-warn"
                                  data-cy="settings-deployment-tab-warning-icon">warning</mat-icon>
                        <mat-icon *ngIf="deployment?.formGroup.dirty && !deployment?.formGroup.invalid"
                                  class="mat-accent"
                                  data-cy="settings-deployment-tab-changed-icon">edit</mat-icon>
                        {{ 'settings_tab_deployment' | translate }}
                    </div>
                </ng-template>
            </mat-tab>
            <mat-tab [label]="TabLabel.Mail">
                <ng-template mat-tab-label>
                    <div data-cy="settings-mail-tab">
                        <mat-icon *ngIf="!loading && mail?.formGroup.invalid"
                                  class="mat-warn"
                                  data-cy="settings-mail-tab-warning-icon">warning</mat-icon>
                        <mat-icon *ngIf="mail?.formGroup.dirty && !mail?.formGroup.invalid"
                                  class="mat-accent"
                                  data-cy="settings-mail-tab-changed-icon">edit</mat-icon>
                        {{ 'settings_tab_mail' | translate }}
                    </div>
                </ng-template>
            </mat-tab>
            <mat-tab [label]="TabLabel.Security">
                <ng-template mat-tab-label>
                    <div data-cy="settings-security-tab">
                        <mat-icon *ngIf="!loading && security?.formGroup.invalid"
                                  class="mat-warn"
                                  data-cy="settings-security-tab-warning-icon">warning</mat-icon>
                        <mat-icon *ngIf="security?.formGroup.dirty && !security?.formGroup.invalid"
                                  class="mat-accent"
                                  data-cy="settings-security-tab-changed-icon">edit</mat-icon>
                        {{ 'settings_tab_security' | translate }}
                    </div>
                </ng-template>
            </mat-tab>
            <mat-tab [label]="TabLabel.Scalability">
                <ng-template mat-tab-label>
                    <div data-cy="settings-scalability-tab">
                        <mat-icon *ngIf="!loading && scalability?.formGroup.invalid"
                                  class="mat-warn"
                                  data-cy="settings-scalability-tab-warning-icon">warning</mat-icon>
                        <mat-icon *ngIf="scalability?.formGroup.dirty && !scalability?.formGroup.invalid"
                                  class="mat-accent"
                                  data-cy="settings-scalability-tab-changed-icon">edit</mat-icon>
                        {{ 'settings_tab_scalability' | translate }}
                    </div>
                </ng-template>
            </mat-tab>
            <mat-tab [label]="TabLabel.NonWorkingDays">
                <ng-template mat-tab-label>
                    <div data-cy="settings-non-working-days-tab">
                        <mat-icon *ngIf="!loading && skippedDays?.formGroup.invalid && hasInvalidCalendars"
                                  class="mat-warn"
                                  data-cy="settings-non-working-days-tab-warning-icon">warning</mat-icon>
                        <mat-icon *ngIf="skippedDays?.formGroup.dirty || hasUnsavedCalendars || (creatingCalendar$ | async)"
                                  class="mat-accent"
                                  data-cy="settings-non-working-days-tab-changed-icon">edit</mat-icon>
                        {{ 'settings_tab_skipped_days' | translate }}
                    </div>
                </ng-template>
            </mat-tab>
        </mat-tab-group>

        <ng-container *uiContentLoading="!settings && loading">
            <ui-general-settings #general
                                 [hidden]="activeTab !== TabLabel.General"
                                 [settings]="settings"
                                 [isLoading]="loading"
                                 [permission]="permission"
                                 [showConnectionString]="isWindowsAuthEnabled">
            </ui-general-settings>
            <ui-deployment-settings #deployment
                                    [hidden]="activeTab !== TabLabel.Deployment"
                                    [settings]="settings"
                                    [isLoading]="loading"
                                    [permission]="permission">
            </ui-deployment-settings>
            <ui-mail-settings #mail
                              [hidden]="activeTab !== TabLabel.Mail"
                              [settings]="settings"
                              [isLoading]="loading"
                              [permission]="permission"
                              (smtpSettingChanged)="onSmtpSettingChanged($event)">
            </ui-mail-settings>
            <ui-security-settings #security
                                  [hidden]="activeTab !== TabLabel.Security"
                                  [settings]="settings"
                                  [isLoading]="loading"
                                  [permission]="permission">
            </ui-security-settings>
            <ui-scalability-settings #scalability
                                     [hidden]="activeTab !== TabLabel.Scalability"
                                     [settings]="settings"
                                     [isLoading]="loading"
                                     [permission]="permission">
            </ui-scalability-settings>
            <ui-skipped-days-settings #skippedDays
                                      [hidden]="activeTab !== TabLabel.NonWorkingDays"
                                      [isLoading]="loading"
                                      [permission]="permission"
                                      [selected]="selectedCalendarIndex"
                                      [(calendars)]="excludedCalendars"
                                      (selectionChange)="selectionChange($event)"
                                      (creatingEntry)="creatingCalendar$.next($event)">
            </ui-skipped-days-settings>
            <div *ngLet="{
                        settingsLoading: !!settings && loading
                    } as state"
                 class="tab-footer">
                <button [uiAuthorize]="permission"
                        [spinnerButtonLoading]="state.settingsLoading"
                        [disableIf]="state.settingsLoading ||
                                 invalid ||
                                 pristine"
                        [hidden]="activeTab === TabLabel.NonWorkingDays && isMultipleCalendarsEnabled"
                        ui-spinner-button
                        mat-flat-button
                        type="submit"
                        color="primary"
                        data-cy="settings-save">
                    {{ 'settings_action_save' | translate }}
                </button>
                <button [hidden]="activeTab !== TabLabel.Mail"
                        [disabled]="invalid"
                        (click)="openModal()"
                        type="button"
                        mat-button>
                    {{ 'settings_mail_action_test' | translate }}
                </button>
            </div>
        </ng-container>
    </form>
</mat-card>
